import React from 'react';
import {
  View,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Image,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { RootStackParamList } from '../../types/navigation';
import { useTheme } from '../../hooks/useTheme';
import DetailContainer from '../../components/shared/DetailContainer';
import CommonHeader from '../../components/common/Header';
import Text from '../../components/common/Text';
import { spacing, radii, fontSize, fontWeight } from '../../themes';

type PaletteGeneratorDetailNavigationProp =
  NativeStackNavigationProp<RootStackParamList>;

// Feature accent colour — a vivid red used specifically for this "coming soon" feature
const FEATURE_ACCENT = '#EF4444';

const PLANNED_FEATURES = [
  { icon: 'color-palette-outline', label: 'Custom colour palette creation' },
  { icon: 'analytics-outline', label: 'Colour harmony analysis' },
  { icon: 'share-outline', label: 'Export and share options' },
  { icon: 'trending-up-outline', label: 'Trend-based recommendations' },
] as const;

const PaletteGeneratorDetailScreen = () => {
  const navigation = useNavigation<PaletteGeneratorDetailNavigationProp>();
  const { colors } = useTheme();

  const rightActions = (
    <View style={styles.comingSoonBadge}>
      <Text size="xs" weight="semibold" style={styles.comingSoonText}>
        Coming Soon
      </Text>
    </View>
  );

  return (
    <View style={{ flex: 1, backgroundColor: colors.background }}>
      <CommonHeader
        title="Colour Palette Generator"
        subtitle="Create harmonious colour palettes for any occasion or style preference"
        showBackButton={true}
        onBackPress={() => navigation.goBack()}
        rightActions={rightActions}
        gradientColors={[colors.linearGradient1, colors.linearGradient2]}
      />
      <DetailContainer style={{ backgroundColor: colors.background }}>
        <ScrollView
          style={styles.scrollView}
          showsVerticalScrollIndicator={false}
        >
          {/* Preview Section */}
          <View style={styles.previewSection}>
            <Text
              size="2xl"
              weight="bold"
              align="center"
              style={[styles.sectionTitle, { color: colors.text }]}
            >
              What&apos;s Coming
            </Text>
            <Image
              source={{ uri: 'https://picsum.photos/400/300?random=13' }}
              style={styles.previewImage}
              resizeMode="cover"
            />
            <Text
              size="md"
              align="center"
              style={[styles.previewDescription, { color: colors.secondary }]}
            >
              Generate custom colour palettes using advanced colour theory and
              harmony rules. Perfect for designers, artists, and style
              enthusiasts.
            </Text>
          </View>

          {/* Features Preview */}
          <View style={[styles.section, { backgroundColor: colors.card }]}>
            <Text
              size="2xl"
              weight="bold"
              align="center"
              style={[styles.sectionTitle, { color: colors.text }]}
            >
              Planned Features
            </Text>
            <View style={styles.featuresList}>
              {PLANNED_FEATURES.map(({ icon, label }) => (
                <View key={label} style={styles.featureItem}>
                  <Ionicons
                    name={icon as keyof typeof Ionicons.glyphMap}
                    size={24}
                    color={FEATURE_ACCENT}
                  />
                  <Text
                    size="md"
                    style={[styles.featureText, { color: colors.text }]}
                  >
                    {label}
                  </Text>
                </View>
              ))}
            </View>
          </View>

          {/* Notify Section */}
          <View style={styles.notifySection}>
            <LinearGradient
              colors={[FEATURE_ACCENT, FEATURE_ACCENT]}
              style={styles.notifyGradient}
            >
              <Text
                size="2xl"
                weight="bold"
                align="center"
                style={[styles.notifyTitle, { color: colors.background }]}
              >
                Be the First to Know
              </Text>
              <Text size="md" align="center" style={styles.notifySubtitle}>
                Get notified when the Colour Palette Generator launches
              </Text>
              <TouchableOpacity
                style={[
                  styles.notifyButton,
                  { backgroundColor: colors.background },
                ]}
              >
                <Text
                  size="md"
                  weight="semibold"
                  style={{ color: FEATURE_ACCENT }}
                >
                  Notify Me
                </Text>
              </TouchableOpacity>
            </LinearGradient>
          </View>
        </ScrollView>
      </DetailContainer>
    </View>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  comingSoonBadge: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    paddingHorizontal: spacing[3],
    paddingVertical: spacing[1],
    borderRadius: radii.lg,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.3)',
  },
  comingSoonText: {
    color: '#FFFFFF',
    fontSize: fontSize.xs,
    fontWeight: fontWeight.semibold,
  },
  previewSection: {
    paddingHorizontal: spacing[5],
    paddingVertical: spacing[7],
  },
  sectionTitle: {
    marginBottom: spacing[6],
  },
  previewImage: {
    width: '100%',
    height: 200,
    borderRadius: radii.lg,
    marginBottom: spacing[4],
  },
  previewDescription: {
    lineHeight: 24,
  },
  section: {
    paddingHorizontal: spacing[5],
    paddingVertical: spacing[7],
  },
  featuresList: {
    gap: spacing[4],
  },
  featureItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[4],
  },
  featureText: {
    flex: 1,
  },
  notifySection: {
    paddingHorizontal: spacing[5],
    paddingVertical: spacing[7],
  },
  notifyGradient: {
    borderRadius: radii.xl,
    paddingVertical: spacing[10],
    paddingHorizontal: spacing[5],
    alignItems: 'center',
  },
  notifyTitle: {
    marginBottom: spacing[2],
  },
  notifySubtitle: {
    color: 'rgba(255, 255, 255, 0.9)',
    marginBottom: spacing[6],
  },
  notifyButton: {
    paddingVertical: spacing[4],
    paddingHorizontal: spacing[8],
    borderRadius: radii.lg,
  },
});

export default PaletteGeneratorDetailScreen;
